import * as React from 'react';
import { useHistory } from 'react-router';
import {
  useUpdateShapeTileMutation,
  useGetShapeTileLazyQuery,
  useGetConfigTilesQuery,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { ToastContext } from '../components/Toast';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { FormActions } from '../components/FormActions';
import { PageHeader } from '../components/PageHeader';
import { Input } from '../components/Input';
import { Validated } from '../components/Validated';
import { useValidation } from '../utils/Validation';
import { uniqueNameValidator } from '../utils/ValidationRules';
import './AdminCreateShapeTilePage.css';

/** タイル一覧のパス。キャンセルと保存の後にこの一覧へ戻る */
const LIST_PATH = '/configs/shape_tiles';

/**
 * 幅・高さ・間隔の単位。初期データのタイル（45角タイルは45×45、45二丁タイルは95×45）が
 * タイルの呼び名の寸法とミリメートルで一致するため、ミリメートルとして表示する
 */
const LENGTH_UNIT = 'mm';

/** 1以上の整数だけを通す。空欄はparseIntでNaNになるので、必須入力として弾く */
function validatePositiveInteger(value: string): string | undefined {
  if (value === '') {
    return '必須入力項目です';
  }
  if (!/^\d+$/.test(value) || parseInt(value, 10) < 1) {
    return '1以上の整数で入力してください';
  }
  return undefined;
}

/** 0以上の整数だけを通す。空欄はparseIntでNaNになるので、必須入力として弾く */
function validateNonNegativeInteger(value: string): string | undefined {
  if (value === '') {
    return '必須入力項目です';
  }
  if (!/^\d+$/.test(value)) {
    return '0以上の整数で入力してください';
  }
  return undefined;
}

/**
 * 入力欄は文字列で持ち、数値として正しいことを確かめてから送信時に整数へ変える。
 * 名称は、登録済みのほかのタイルと同じ名称を入力の誤りとして弾く
 */
function buildTileRules(otherTileNames: string[]) {
  return {
    name: uniqueNameValidator(otherTileNames),
    width: validatePositiveInteger,
    height: validatePositiveInteger,
    margin: validateNonNegativeInteger,
  };
}

interface ConfigCreateShapeTilePageProps {
  existingId?: number;
}

interface ShapeTileFormTexts {
  title: string;
  submitLabel: string;
  completedMessage: string;
  failedMessage: string;
}

/** 新規登録と編集で、見出し・送信ボタン・通知の文言を出し分ける */
function getFormTexts(isNew: boolean): ShapeTileFormTexts {
  if (isNew) {
    return {
      title: 'タイルの登録',
      submitLabel: '登録',
      completedMessage: 'タイルを登録しました',
      failedMessage: 'タイルの登録に失敗しました',
    };
  }
  return {
    title: 'タイルの編集',
    submitLabel: '保存',
    completedMessage: 'タイルを保存しました',
    failedMessage: 'タイルの保存に失敗しました',
  };
}

interface LengthInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  validationMessage?: string;
  min: number;
}

/** エラーメッセージが出ているときだけ、入力欄からメッセージの要素を参照させる */
function getDescribedBy(validationMessage: string | undefined, messageId: string): string | undefined {
  if (validationMessage) {
    return messageId;
  }
  return undefined;
}

/**
 * 長さの入力欄。Inputと同じラベルと検証表示の形で、入力欄の後ろに単位を表示する
 */
const LengthInput: React.FC<LengthInputProps> = function (props) {
  const messageId = `${props.id}-message`;
  return (
    <div className="App-input_container">
      <label className="App-label" htmlFor={props.id}>
        {props.label}
        <span className="App-label_required" aria-hidden="true">
          *
        </span>
      </label>
      <div className="AdminCreateShapeTilePage-length_row">
        <Validated message={props.validationMessage} messageId={messageId}>
          <input
            id={props.id}
            className="AdminCreateShapeTilePage-length_input"
            type="number"
            min={props.min}
            step={1}
            aria-required={true}
            aria-invalid={Boolean(props.validationMessage)}
            aria-describedby={getDescribedBy(props.validationMessage, messageId)}
            onChange={(event) => props.onChange(event.target.value)}
            value={props.value}
          />
        </Validated>
        <span className="AdminCreateShapeTilePage-unit">{LENGTH_UNIT}</span>
      </div>
    </div>
  );
};

export const ConfigCreateShapeTilePage: React.FC<ConfigCreateShapeTilePageProps> = function (
  props
) {
  const isNew = !props.existingId;
  const texts = getFormTexts(isNew);

  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const configTilesResult = useGetConfigTilesQuery({
    fetchPolicy: 'network-only',
  });
  // 編集中のタイル自身は同名判定の対象から外す
  const otherTileNames = (configTilesResult.data?.getConfig.tiles ?? [])
    .filter((tile) => tile.id !== props.existingId)
    .map((tile) => tile.name);
  const validation = useValidation(buildTileRules(otherTileNames));
  const [name, setName] = validation.useState('name', '');
  const [width, setWidth] = validation.useState('width', '');
  const [height, setHeight] = validation.useState('height', '');
  const [margin, setMargin] = validation.useState('margin', '');
  const [getShapeTile, getShapeTileResult] = useGetShapeTileLazyQuery({
    onCompleted(data) {
      const tile = data.getShapeTile;
      setName(tile.name);
      setWidth(tile.width.toString());
      setHeight(tile.height.toString());
      setMargin(tile.margin.toString());
    },
  });
  const [updateShapeTile, updateShapeTileResult] = useUpdateShapeTileMutation({
    onCompleted() {
      toastContext.showToast(texts.completedMessage, 'success');
      history.push(LIST_PATH);
    },
    onError() {
      toastContext.showToast(texts.failedMessage, 'error');
    },
  });

  // 同名判定に既存タイル一覧が要るため、読み込みを待ってから入力欄を出す
  if (configTilesResult.error) {
    return <GraphQLErrorDialog error={configTilesResult.error} />;
  }

  if (configTilesResult.loading || !configTilesResult.data) {
    return <Loading />;
  }

  if (props.existingId) {
    if (!getShapeTileResult.called) {
      getShapeTile({
        variables: {
          shapeTileId: props.existingId,
        },
      });
    }

    if (getShapeTileResult.error) {
      return <GraphQLErrorDialog error={getShapeTileResult.error} />;
    }

    if (getShapeTileResult.loading || !getShapeTileResult.data) {
      return <Loading />;
    }
  }

  return (
    <div className="AdminCreateShapeTilePage">
      <PageHeader
        title={texts.title}
        breadcrumbs={[
          { label: '共通設定' },
          { label: 'タイル設定', to: LIST_PATH },
          { label: texts.title },
        ]}
      />
      <div className="AdminCreateShapeTilePage-form">
        <Input
          label="名称"
          className="AdminCreateShapeTilePage-name_input"
          type="text"
          required={true}
          onChange={(event) => {
            const name = event.target.value;
            setName(name);
          }}
          value={name}
          validationMessage={validation.get('name')}
        />
        <LengthInput
          id="AdminCreateShapeTilePage-width"
          label="幅"
          min={1}
          onChange={(value) => setWidth(value)}
          value={width}
          validationMessage={validation.get('width')}
        />
        <LengthInput
          id="AdminCreateShapeTilePage-height"
          label="高さ"
          min={1}
          onChange={(value) => setHeight(value)}
          value={height}
          validationMessage={validation.get('height')}
        />
        <LengthInput
          id="AdminCreateShapeTilePage-margin"
          label="間隔"
          min={0}
          onChange={(value) => setMargin(value)}
          value={margin}
          validationMessage={validation.get('margin')}
        />
        <FormActions
          submitLabel={texts.submitLabel}
          submitDisabled={validation.isInvalid || updateShapeTileResult.loading}
          onSubmit={() => {
            updateShapeTile({
              variables: {
                input: {
                  id: props.existingId,
                  name,
                  width: parseInt(width, 10),
                  height: parseInt(height, 10),
                  margin: parseInt(margin, 10),
                },
              },
            });
          }}
          onCancel={() => history.push(LIST_PATH)}
        />
      </div>
    </div>
  );
};
